import Chart from '../components/chart';
import { truncateOrDefault, isFirstOccurrence, compareX } from './utils';
import * as dateLabels from './date-labels';
import * as datesHelper from './dates';

const COLOR_PALETTES = {
  gallup: ['#4d9c2d', '#012168', '#00827d', '#b24b58'],
  polcategorical: ['#4d9c2d', '#00827d', '#b24b58', '#692044'],
  democratic: ['#012168', '#3e8ddd', '#0055b7', '#002a48'],
  republican: ['#f42434', '#ab162b', '#79232e', '#51252f'],
  independent: ['#8a8b8c', '#404040', '#666666', '#2b2b2b'],
  disapproval: ['#51252f', '#b24b58', '#00827d', '#4d9c2d'],
  approvalFlexchart: ['#4d9c2d', '#692044'],
};

const DASH_STYLES = {
  gallup: [null, null, '5 5', '2 2'],
  polcategorical: [null, '5 5', null, '2 2'],
  democratic: [null, null, '5 5', '2 2'],
  republican: [null, '5 5', '2 2', null],
  independent: [null, '5 5', '2 2', null],
};
const DEFAULT_SOURCE = 'Gallup';
const TITLE_CHARS = 150;
const SOURCE_CHARS = 40;
const LEGEND_ITEM_CHARS = 50;

export function validateData(data) {
  const warnings = [];
  const errors = [];
  let valid = true;

  // Test: Data contains lines
  if (!data.lines || !data.lines.length) {
    warnings.push(['Data contains no lines', Chart.ERROR_INCOMPLETE_DATA]);
    valid = false;
  } else {
    // Test: Lines are valid
    data.lines.forEach((line, lineIndex) => {
      const lineDesc = `Line #${lineIndex + 1} (${line.title || 'untitled'})`;
      // Test: Lines have points
      if (!line.points || !line.points.length) {
        warnings.push([
          `${lineDesc} contains no points`,
          Chart.ERROR_INCOMPLETE_DATA,
          { line },
        ]);
        valid = false;
      }

      // Test: Lines have more than 1 point
      if (line.points.length === 1) {
        warnings.push([
          `${lineDesc} only contains 1 point (need at least 2)`,
          Chart.ERROR_INCOMPLETE_DATA,
          { line },
        ]);
        valid = false;
      }

      // Test: Points are valid
      line.points.forEach((point, pointIndex) => {
        // Check if point is a number
        if (!isNaN(point)) {
          // do nothing
        } else if ('x' in point) {
          if (isNaN(point.x)) {
            errors.push([
              `The X-coordinate of point #${
                pointIndex + 1
              } on ${lineDesc} is not a valid number: ${point.x}`,
              Chart.ERROR_INVALID_DATA,
              { point },
            ]);
            valid = false;
          }

          if ('y' in point) {
            if (isNaN(point.y)) {
              errors.push([
                `The Y-coordinate of point #${
                  pointIndex + 1
                } on ${lineDesc} is not a valid number: ${point.y}`,
                Chart.ERROR_INVALID_DATA,
                { point },
              ]);
              valid = false;
            }
          } else if ('date' in point) {
            if (!(point.date instanceof Date) || isNaN(point.date)) {
              errors.push([
                `The date value of point #${
                  pointIndex + 1
                } on ${lineDesc} is not a valid date: ${point.date}`,
                Chart.ERROR_INVALID_DATA,
                { point },
              ]);
              valid = false;
            }
          }
        } else {
          errors.push([
            `Point #${
              pointIndex + 1
            } on ${lineDesc} is in an invalid format: ${point}`,
            Chart.ERROR_INVALID_DATA,
            { point },
          ]);
          valid = false;
        }
      });
    });
  }

  // Test: Data contains labels
  if (!data.labels || !data.labels.length) {
    warnings.push(['Data contains no labels', Chart.ERROR_INCOMPLETE_DATA]);
    valid = false;
  } else {
    // Test: Labels are valid
    data.labels.forEach((label, labelIndex) => {
      if (typeof label !== 'string' && !label.text) {
        errors.push([
          `Label #${labelIndex + 1} is in an invalid format: ${label}`,
          Chart.ERROR_INVALID_DATA,
        ]);
        valid = false;
      }
    });

    // Test: Labels are consistently formatted
    const formatTests = [
      // number
      (label) => !isNaN(label),
      // standard date format
      (label) => dateLabels.STD_DATE_FORMAT.test(label),
      // standard date range format
      (label) => dateLabels.STD_DATE_RANGE_FORMAT.test(label),
      // standard month format
      (label) => dateLabels.STD_MONTH_FORMAT.test(label),
    ];

    const firstLabel = data.labels[0].text || data.labels[0];
    let test;

    for (let i = 0; i < formatTests.length; i++) {
      if (formatTests[i](firstLabel)) {
        test = formatTests[i];
        break;
      }
    }

    if (test && !data.labels.every(test)) {
      errors.push([
        'Labels are not formatted consistently',
        Chart.ERROR_INVALID_DATA,
        { labels: data.labels },
      ]);
      valid = false;
    }
  }

  return { valid, warnings, errors };
}

export function normalizeData(data, chartCodeName) {
  const normalized = {
    title: truncateOrDefault(data.title, TITLE_CHARS),
    subtitle: truncateOrDefault(data.subtitle, -1, null),
    footnote: truncateOrDefault(data.footnote, -1, null),
    source: truncateOrDefault(data.source, SOURCE_CHARS, DEFAULT_SOURCE),
    labels: [],
    lines: [],
  };
  const lines = (data.lines || []).map((line) =>
    Object.assign({ points: [] }, line)
  );
  let labels = data.labels || [];
  let xValues;

  // Check for { date: <date_obj>, y: <number> } points format
  // If points are in this format, labels do not need to be provided
  if (lines.length && lines[0].points[0] && lines[0].points[0].date) {
    const dates = lines
      .map((line) => line.points)
      .reduce((a, b) => a.concat(b), [])
      .map((point) => point.date)
      .sort((a, b) => a - b);

    // Labels are still relied upon at this point, so we need to generate them
    labels = dates.map(datesHelper.standardFormat).filter(isFirstOccurrence);
    xValues = dates.map(datesHelper.epochDays).filter(isFirstOccurrence);
    normalized.labelType = 'date';

    // Check if labels are either strings in standard date format or numerical
  } else if (labels.length && typeof labels[0] !== 'object') {
    // Check for standard date format or numbers
    if (dateLabels.STD_DATE_FORMAT.test(labels[0].toString().trim())) {
      xValues = labels.map((label) => datesHelper.epochDays(new Date(label)));
      normalized.labelType = 'date';
    } else if (dateLabels.STD_MONTH_FORMAT.test(labels[0].toString().trim())) {
      xValues = labels.map((label) =>
        datesHelper.epochDays(new Date(`${label} 1`))
      );
      normalized.labelType = 'date';
    } else if (
      dateLabels.STD_DATE_RANGE_FORMAT.test(labels[0].toString().trim())
    ) {
      xValues = labels.map((label) =>
        datesHelper.epochDays(new Date(label.substr(label.indexOf('-') + 1)))
      );
      normalized.labelType = 'date_range';
    } else if (!isNaN(labels[0])) {
      xValues = labels.map((label) => Number(label));
      normalized.labelType = 'number';
    }
  } else {
    normalized.labelType = 'string';
  }

  // Normalize label format to { x: <number>, text: <label> }
  if (typeof labels[0] !== 'object') {
    labels.forEach((label, index) => {
      if (label === null || label === '-') return;

      normalized.labels.push({
        x: xValues ? xValues[index] : index,
        text: String(label),
      });
    });
  } else {
    normalized.labels = labels;
  }

  // Sort labels by x-value (in case they aren't already)
  normalized.labels.sort(compareX);

  normalized.labelsLookup = {};
  for (let i = 0; i < normalized.labels.length; i++) {
    normalized.labelsLookup[normalized.labels[i].x] = normalized.labels[i].text;
  }

  // Hash table for line color lookup
  const colorIndices = {};
  Object.keys(COLOR_PALETTES).forEach((palette) => {
    colorIndices[COLOR_PALETTES[palette]] = 0;
  });

  // Parse line data
  lines.forEach((line, lineIndex) => {
    const graphLine = {
      id: line.id || lineIndex,
      title: (line.title || '').slice(0, LEGEND_ITEM_CHARS),
    };

    // Normalize point format
    if (typeof line.points[0] === 'object' && line.points[0] !== null) {
      if (line.points[0].date) {
        graphLine.points = line.points.map((p, i) => ({
          x: xValues[i],
          y: p.y,
        }));
      } else {
        graphLine.points = line.points;
      }
    } else {
      // Otherwise this should just be an array of numbers,
      // so let's put it in the aforementioned object format
      graphLine.points = [];
      line.points.forEach((point, index) => {
        // Nulls and hyphens represent gaps in data
        if (point === null || point === '-') return;

        graphLine.points.push({
          x: xValues ? xValues[index] : index,
          y: Number(point),
        });
      });
    }

    // Sort points by x-value (in case they aren't already)
    graphLine.points.sort(compareX);

    // Figure out line colors
    let palette;
    let dashStyles;
    if (!line.color) {
      graphLine.colorPalette = 'gallup';
      palette = COLOR_PALETTES.gallup;
    } else if (typeof line.color === 'string' && COLOR_PALETTES[line.color]) {
      graphLine.colorPalette = line.color;
      palette = COLOR_PALETTES[line.color];
    } else if (line.colorPalette && COLOR_PALETTES[line.colorPalette]) {
      graphLine.colorPalette = line.colorPalette;
      palette = COLOR_PALETTES[line.colorPalette];
    } else if (line.color instanceof Array) {
      palette = line.color;
    }

    if (chartCodeName) {
      graphLine.colorPalette = 'approvalFlexchart';
      palette = COLOR_PALETTES.approvalFlexchart;
    }

    if (!line.strokeDashArray) {
      dashStyles = DASH_STYLES[graphLine.colorPalette];
    } else if (typeof line.color === 'string' && DASH_STYLES[line.color]) {
      dashStyles = DASH_STYLES[line.color];
    } else if (line.colorPalette && DASH_STYLES[line.colorPalette]) {
      dashStyles = DASH_STYLES[line.colorPalette];
    } else if (line.strokeDashArray === 'string') {
      dashStyles = line.strokeDashArray;
    }

    if (palette) {
      if (!colorIndices[palette]) {
        colorIndices[palette] = 0;
      }

      graphLine.color = palette[colorIndices[palette]];

      if (dashStyles) {
        graphLine.strokeDashArray = dashStyles[colorIndices[palette]];
      }
      colorIndices[palette]++;
    } else {
      graphLine.color = line.color.toLowerCase();
      graphLine.strokeDashArray = line.strokeDashArray;
    }

    normalized.lines.push(graphLine);
  });

  return normalized;
}
